import { BankingTransactionEntity } from '@/generated/graphql.ts';
import { COLORS } from '@/helpers/consts.ts';

export enum FileTypeEnum {
  Spreadsheet = 'spreadsheet',
  Excel = 'excel',
  Xlsx = '.xlsx',
  Xls = '.xls',
}

export enum DocumentTypeEnum {
  Document = 'document',
  Word = 'word',
  Docx = '.docx',
  Doc = '.doc',
}

export enum ImageTypeEnum {
  Image = 'image/',
}

export enum ZipTypeEnum {
  Zip = 'application/zip',
  XZipCompressed = 'application/x-zip-compressed',
}

export enum PdfTypeEnum {
  Pdf = 'application/pdf',
}

export interface ITicketsTableFilters {
  chatFileIds?: string[];
  chatIds?: string[];
  chatPeriodIds?: string[];
  chatTransactionIds?: string[];
  companyIds?: string[];
  messageFileIds?: string[];
  messagePeriodIds?: string[];
  messageTransactionIds?: string[];
  status?: ChatStatusEnum[];
}

export interface ITicketsFiltersRequestParams {
  page: number;
  rowsPerPage: number;
  filters?: ITicketsTableFilters;
}

export interface ITicketFiltersRequestParams {
  filters?: ITicketsTableFilters;
}

export interface CreateTicketPayload {
  companyId: string;
  periodFolderId: string;
  subject: string;
}

export enum ChatStatusEnum {
  Closed = 'CLOSED',
  Open = 'OPEN',
  Review = 'REVIEW'
}

export const ALL_CHAT_STATUSES: ChatStatusEnum[] = [
  ChatStatusEnum.Open,
  ChatStatusEnum.Review,
  ChatStatusEnum.Closed,
];

export const resolveTicketsStatusFilter = (selected: ChatStatusEnum | ''): ChatStatusEnum[] => {
  return selected === '' ? ALL_CHAT_STATUSES : [selected];
};

export const TICKET_STATUS_COLORS = {
  OPEN: COLORS.RED,
  CLOSED: COLORS.TEAL,
  REVIEW: COLORS.BLUE,
};

export interface ITicketStatusOption {
  id: ChatStatusEnum;
  title: string;
  color: string;
}

export interface ITicketFilterStatusOption {
  id: ChatStatusEnum | '';
  title: string;
  color: string;
}

export const TICKET_FILTER_STATUS_OPTIONS: ITicketFilterStatusOption[] = [
  {
    id: '',
    title: 'All',
    color: COLORS.GREY,
  },
  {
    id: ChatStatusEnum.Closed,
    title: 'Resolved',
    color: TICKET_STATUS_COLORS.CLOSED,
  },
  {
    id: ChatStatusEnum.Review,
    title: 'Review',
    color: TICKET_STATUS_COLORS.REVIEW,
  },
  {
    id: ChatStatusEnum.Open,
    title: 'Open',
    color: TICKET_STATUS_COLORS.OPEN,
  },
];

export const TICKET_STATUS_OPTIONS: ITicketStatusOption[] = [
  {
    id: ChatStatusEnum.Closed,
    title: 'Resolved',
    color: TICKET_STATUS_COLORS.CLOSED,
  },
  {
    id: ChatStatusEnum.Review,
    title: 'Review',
    color: TICKET_STATUS_COLORS.REVIEW,
  },
  {
    id: ChatStatusEnum.Open,
    title: 'Open',
    color: TICKET_STATUS_COLORS.OPEN,
  },
];

export type GetUnreadCountByPeriodResult = {
  getUnreadCountByPeriod?: {
    totalUnreadCount: number;
    unreadMessages: Array<{
      chatId: string;
      unreadByUserCount: number;
    }>;
  };
} | null;

export interface ITicketFileContext {
  id: string;
  name: string;
}

/** Outcome of the "review before sending" validation gate. */
export enum MessageGateAction {
  Send = 'send',
  Edit = 'edit',
  Cancel = 'cancel',
}

/**
 * What kind of text is being validated — tunes the AI prompt.
 * Values are the GraphQL `ValidationKind` enum names sent over the wire.
 */
export enum ValidationKind {
  Message = 'MESSAGE',
  Subject = 'SUBJECT',
}

/** Mirrors the backend MAX_DRAFT_LEN: above this the text is too long to check reliably. */
export const MAX_VALIDATION_TEXT_LENGTH = 4000;

/** One reviewable field (subject or message) shown in the combined create-ticket review. */
export interface MessageReviewSection {
  key: string;
  label: string;
  original: string;
  improvedEn: string;
  translation: string;
  translationLanguage: string;
  issues: MessageValidationIssue[];
  /** Non-English words quoted from the draft, so the author sees what the client could not read. */
  foreignFragments: string[];
}

/** Codes returned by the AI text validation (accounting.validateClientText). */
export type MessageValidationIssue =
  | 'non_english'
  | 'mixed_language'
  | 'translit'
  | 'rude_tone'
  | 'grammar'
  | 'unclear';

/** Result of validating a whole ticket draft (subject + first message) in one backend call. */
export interface ValidateTicketDraftResult {
  subject: ValidateClientTextResult;
  message: ValidateClientTextResult;
}

export interface ValidateClientTextResult {
  needs_improvement: boolean;
  issues: MessageValidationIssue[];
  language_detected: string;
  /** Non-English words quoted from the draft (proper nouns excluded). */
  foreign_fragments: string[];
  improved_en: string;
  /** Back-translation of improved_en into the accountant's source language (empty if the draft was English). */
  translation: string;
  /** English name of the source language for `translation` (e.g. "Russian", "Polish"); empty if English. */
  translation_language: string;
}

/** Human-readable English labels for issue chips shown in the validation dialog. */
export const MESSAGE_VALIDATION_ISSUE_LABELS: Record<MessageValidationIssue, string> = {
  non_english: 'Not in English',
  mixed_language: 'Not fully in English',
  translit: 'Russian in Latin letters',
  rude_tone: 'Tone sounds harsh',
  grammar: 'Grammar / spelling',
  unclear: 'Unclear message',
};

export type ITicketTransactionContext = BankingTransactionEntity;
